// platform-save.js — save a Blob to the user's device on every platform.
//
// Web/PWA: classic <a download> flow (goes to the browser's Downloads).
// Native (Capacitor WebView): <a download> does NOT work for blob: URLs, so we
// stream the blob into the app's external files directory with chunked base64
// appends (large recordings must never be base64-encoded in one piece — that
// doubles memory and OOMs on 2–4 GB videos), then hand it to the system share
// sheet so the user can send it to WhatsApp / Gallery / Drive.
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Media } from '@capacitor-community/media';
import { isNativeApp } from './platform.js';
import { ensureMediaAlbum } from './media/native-media.js';

const CHUNK_BYTES = 6 * 1024 * 1024; // 6 MB slices keep base64 peaks small

// Track last saved file URI for optional sharing later
let _lastSavedUri = null;
let _lastSavedFileName = null;

export function getLastSavedUri() { return _lastSavedUri; }
export function getLastSavedFileName() { return _lastSavedFileName; }

function blobSliceToBase64(slice) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read file data'));
    reader.readAsDataURL(slice);
  });
}

function sanitizeFileName(name) {
  return String(name || 'download').replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_');
}

function webDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * Save a blob. Returns { ok, method, uri?, error? }.
 * onProgress(fraction 0..1) is reported for native chunked writes.
 * @param {Blob} blob
 * @param {string} fileName
 * @param {{onProgress?: Function, subDir?: string}} [options]
 */
export async function saveBlobToDevice(blob, fileName, { onProgress, subDir = 'CricketScorer' } = {}) {
  const actualExt = blob.type.includes('mp4') ? 'mp4' : 'webm';
  let safeName = sanitizeFileName(fileName);
  if (!safeName.endsWith(`.${actualExt}`)) {
    safeName = safeName.replace(/\.[^/.]+$/, '') + `.${actualExt}`;
  }

  if (!isNativeApp) {
    webDownload(blob, safeName);
    return { ok: true, method: 'download' };
  }

  // Request native filesystem permissions first
  try {
    const status = await Filesystem.checkPermissions();
    if (status.publicStorage !== 'granted') {
      await Filesystem.requestPermissions();
    }
  } catch (permErr) {
    console.warn('[Filesystem] Permission check failed:', permErr);
  }

  // Directory priorities: External -> Documents -> Cache -> Data
  // External puts files in /sdcard/CricketScorer/ which is visible in file managers
  // and accessible to MediaStore (Gallery indexing).
  const targetDirectories = [
    { dir: Directory.External, path: `${subDir}/${safeName}` },
    { dir: Directory.Documents, path: `${subDir}/${safeName}` },
    { dir: Directory.Cache, path: safeName },
    { dir: Directory.Data, path: safeName }
  ];

  let lastError = null;
  for (const { dir, path } of targetDirectories) {
    // Set when the app-side copy has been handed over to the gallery and
    // deleted, so the Share button knows the file URI is no longer valid.
    let galleryOnly = false;
    const tempPath = `${path}.tmp`;
    try {
      await Filesystem.writeFile({ path: tempPath, data: '', directory: dir, recursive: true });
      try {
        for (let offset = 0; offset < blob.size; offset += CHUNK_BYTES) {
          const base64 = await blobSliceToBase64(blob.slice(offset, offset + CHUNK_BYTES));
          await Filesystem.appendFile({ path: tempPath, data: base64, directory: dir });
          if (onProgress) onProgress(Math.min(1, (offset + CHUNK_BYTES) / blob.size));
          await new Promise(r => setTimeout(r, 0)); // Yield macro-task to GC
        }
        // Atomic rename ONLY after 100% complete chunk write
        await Filesystem.rename({ from: tempPath, to: path, directory: dir });
      } catch (appendErr) {
        await Filesystem.deleteFile({ path: tempPath, directory: dir }).catch(() => {});
        throw appendErr;
      }
      let { uri } = await Filesystem.getUri({ path, directory: dir });
      console.log(`[Filesystem] Saved successfully to target directory:`, uri);
      // Copy videos into an actual Android media album. The Media plugin
      // requires the platform identifier returned by getAlbums(), not a
      // display name such as "CricketScorer".
      try {
        if (safeName.endsWith('.mp4') || safeName.endsWith('.webm')) {
          const perms = await Media.checkPermissions();
          if (perms.photos !== 'granted') { await Media.requestPermissions(); }
          const albumIdentifier = await ensureMediaAlbum(Media, subDir);
          await Media.saveVideo({
            path: uri,
            albumIdentifier,
            fileName: safeName
          });
          console.log('[Media] Saved video indexed in gallery under album:', subDir);

          // STORAGE DOUBLING FIX. Media.saveVideo COPIES the file into the
          // MediaStore, so a 2 GB match was occupying 4 GB: once in
          // /sdcard/CricketScorer/ and again in the gallery. (The previous
          // comment here claimed Directory.Cache made Android reclaim it
          // automatically — untrue: the first target is Directory.External,
          // which the OS never evicts.)
          //
          // The gallery copy is the one the user actually browses and shares,
          // so drop the app-side duplicate once the copy is confirmed.
          try {
            await Filesystem.deleteFile({ path, directory: dir });
            console.log('[Filesystem] Removed the duplicate app-side copy; gallery holds the video.');
            galleryOnly = true;
          } catch (cleanupErr) {
            // Non-fatal: worst case we are back to the old double-storage
            // behaviour, and the user still has their video.
            console.warn('[Filesystem] Could not remove the duplicate copy:', cleanupErr);
          }
        }
      } catch (mediaErr) {
        console.warn('[Media] Gallery indexing warning (file remains in filesystem):', mediaErr);
      }
      // A deleted app-side copy leaves the URI dangling — sharing it would fail
      // with a confusing error, so clear it and let the user share from the
      // gallery instead.
      _lastSavedUri = galleryOnly ? null : uri;
      _lastSavedFileName = safeName;
      return { ok: true, method: galleryOnly ? 'gallery' : 'filesystem', uri: galleryOnly ? null : uri, galleryOnly };
    } catch (err) {
      console.warn(`[Filesystem] Write attempt failed, trying next fallback:`, err);
      lastError = err;
    }
  }

  // Blob URL downloads are a no-op in many Android WebViews. Never report that
  // path as a successful native save, because callers would then delete the
  // crash-recovery chunks even though no file reached the user.
  return {
    ok: false,
    method: 'filesystem',
    error: lastError || new Error('Native filesystem save failed; recovery data was kept.')
  };
}

/** Open the system share sheet for a previously saved native file URI. */
export async function shareSavedFile(uri, title = 'Cricket Scorer') {
  if (!isNativeApp || !uri) return false;
  try {
    await Share.share({ title, url: uri });
    return true;
  } catch (_) {
    return false; // user dismissed the sheet — not an error
  }
}

/** Share the last saved recording file (called from UI "Share" button). */
export async function shareLastRecording() {
  if (_lastSavedUri) {
    return shareSavedFile(_lastSavedUri, _lastSavedFileName || 'Cricket Recording');
  }
  return false;
}

/** Convenience: save small JSON payloads (match exports). */
export async function saveJsonToDevice(data, fileName) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const result = await saveBlobToDevice(blob, fileName);
  if (result.ok && result.uri) await shareSavedFile(result.uri, fileName);
  return result;
}
